import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { usePageWelcome } from '../hooks/usePageWelcome';
import WelcomeModal from '../components/WelcomeModal';
import { getJournalEntries, saveJournalEntry, deleteJournalEntry } from '../utils/journalService';
import { getWelcomeModalStatus, markWelcomeModalAsSeen } from '../utils/syncService';

const CheckInJournal = () => {
  const { showWelcome, closeWelcome } = usePageWelcome('journal');
  const [showAleraWelcome, setShowAleraWelcome] = useState(false);
  // Check for main Alera welcome on first navigation
  useEffect(() => {
    const checkWelcomeModal = async () => {
      try {
        const status = localStorage.getItem('memberStatus');
        const shouldShowOnFirstNav = localStorage.getItem('showWelcomeOnFirstNav') === 'true';
        const userEmail = localStorage.getItem('userEmail');
        
        if (status === 'active' && shouldShowOnFirstNav && userEmail) {
          // Check Supabase for welcome modal status
          const result = await getWelcomeModalStatus(userEmail);
          
          if (result.success && !result.hasSeen) {
            setTimeout(() => {
              setShowAleraWelcome(true);
            }, 300);
          }
          
          localStorage.removeItem('showWelcomeOnFirstNav');
        }
      } catch (e) {}
    };
    
    checkWelcomeModal();
  }, []);

  const closeAleraWelcome = async () => {
    const userEmail = localStorage.getItem('userEmail');
    
    if (userEmail) {
      // Save to Supabase for cross-device sync
      await markWelcomeModalAsSeen(userEmail);
    }
    
    // Also save to localStorage as backup
    try { localStorage.setItem('welcomeModalSeen', 'true'); } catch (e) {}
    setShowAleraWelcome(false);
  };

  const [entries, setEntries] = useState([]);
  const [showNewEntryModal, setShowNewEntryModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [newEntry, setNewEntry] = useState({
    prompt: '',
    response: '',
    mood: '',
    isPrivate: true,
    tags: []
  });
  const [viewingEntry, setViewingEntry] = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load entries from Supabase
  useEffect(() => {
    const loadEntries = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) return;

      setLoadingEntries(true);
      try {
        const result = await getJournalEntries(userEmail);
        if (result.success) {
          setEntries(result.data);
          console.log('Loaded journal entries from Supabase:', result.data.length);
        }
      } catch (error) {
        console.error('Error loading journal entries:', error);
      } finally {
        setLoadingEntries(false);
      }
    };

    loadEntries();
  }, []);

  const reflectionPrompts = [
    {
      id: 1,
      category: 'Weekly Check-in',
      icon: '📅',
      color: 'from-blue-100 to-cyan-100',
      prompts: [
        'What were three wins from this week, no matter how small?',
        'What challenged me this week, and what did I learn from it?',
        'How did I prioritize my wellbeing this week?',
        'What am I grateful for from the past seven days?'
      ]
    },
    {
      id: 2,
      category: 'Physical Wellbeing',
      icon: '💪',
      color: 'from-emerald-100 to-teal-100',
      prompts: [
        'How is my body feeling today? What is it telling me?',
        'What movement brought me joy this week?',
        'How have my energy levels been? What patterns do I notice?',
        'What foods made me feel nourished and energized?'
      ]
    },
    {
      id: 3,
      category: 'Emotional Wellbeing',
      icon: '💗',
      color: 'from-rose-100 to-pink-100',
      prompts: [
        'What emotions have I been experiencing most this week?',
        'When did I feel most at peace? What was I doing?',
        'What do I need to let go of to feel lighter?',
        'How have I shown myself compassion this week?'
      ]
    },
    {
      id: 4,
      category: 'Progress & Growth',
      icon: '🌱',
      color: 'from-sage-100 to-emerald-100',
      prompts: [
        'What progress have I made toward my health goals?',
        'What new habit am I proud of building?',
        'How have I stepped outside my comfort zone?',
        'What would I tell my past self about this journey?'
      ]
    },
    {
      id: 5,
      category: 'Self-Care',
      icon: '✨',
      color: 'from-purple-100 to-violet-100',
      prompts: [
        'What act of self-care did I prioritize this week?',
        'When did I say "no" to protect my energy?',
        'What brings me joy that I want more of?',
        'How can I be kinder to myself this coming week?'
      ]
    }
  ];

  const moodOptions = [
    { emoji: '😊', label: 'Great', color: 'bg-green-100 text-green-700' },
    { emoji: '🙂', label: 'Good', color: 'bg-teal-100 text-teal-700' },
    { emoji: '😐', label: 'Okay', color: 'bg-yellow-100 text-yellow-700' },
    { emoji: '😔', label: 'Low', color: 'bg-orange-100 text-orange-700' },
    { emoji: '😢', label: 'Struggling', color: 'bg-red-100 text-red-700' }
  ];

  const tagOptions = [
    'Gratitude', 'Progress', 'Challenge', 'Breakthrough', 'Self-Care',
    'Healing', 'Energy', 'Mindfulness', 'Community', 'Victory'
  ];

  const startNewEntry = (prompt, category) => {
    setNewEntry({
      prompt: prompt,
      category: category,
      response: '',
      mood: '',
      isPrivate: true,
      tags: []
    });
    setShowNewEntryModal(true);
  };

  const saveEntry = async () => {
    if (!newEntry.response.trim() || !newEntry.mood) return;

    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      alert('Please log in to save journal entries');
      return;
    }

    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    
    const entry = {
      id: Date.now(),
      ...newEntry,
      date: new Date().toISOString(),
      response: newEntry.response.trim()
    };

    // Optimistic update
    setEntries([entry, ...entries]);
    setShowNewEntryModal(false);

    // Save to Supabase
    try {
      const result = await saveJournalEntry(userEmail, {
        entry_date: today,
        mood: newEntry.mood,
        notes: newEntry.response.trim(),
        // Map old structure to new if needed
        gratitude: newEntry.tags.includes('gratitude') ? newEntry.response : null,
        challenges: newEntry.tags.includes('challenges') ? newEntry.response : null,
        wins: newEntry.tags.includes('wins') ? newEntry.response : null
      });

      if (!result.success) {
        console.error('Failed to save journal entry:', result.error);
        // Could revert optimistic update here if needed
      } else {
        console.log('Journal entry saved to Supabase');
      }
    } catch (error) {
      console.error('Error saving journal entry:', error);
    }

    setNewEntry({
      prompt: '',
      response: '',
      mood: '',
      isPrivate: true,
      tags: []
    });
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    
    const userEmail = localStorage.getItem('userEmail');
    const entryToDelete = entries.find(e => e.id === id);
    
    // Optimistic update
    setEntries(entries.filter(e => e.id !== id));

    // Delete from Supabase
    if (userEmail && entryToDelete) {
      try {
        const entryDate = new Date(entryToDelete.date).toISOString().split('T')[0];
        const result = await deleteJournalEntry(userEmail, entryDate);
        
        if (!result.success) {
          console.error('Failed to delete journal entry:', result.error);
          // Revert on error
          setEntries([entryToDelete, ...entries.filter(e => e.id !== id)]);
        }
      } catch (error) {
        console.error('Error deleting journal entry:', error);
        // Revert on error
        setEntries([entryToDelete, ...entries.filter(e => e.id !== id)]);
      }
    }
  };

  const toggleTag = (tag) => {
    setNewEntry(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const shareToForum = (entry) => {
    // This would integrate with the forum - for now just show confirmation
    alert('This entry will be shared to the Community Forum (feature coming soon!)');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 pt-20 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <RouterLink
              to="/dashboard"
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </RouterLink>
            <div>
              <h1 className="text-4xl font-bold text-charcoal-800">Check-in Journal</h1>
              <p className="text-charcoal-600 text-sm mt-1">Your private space for reflection and growth</p>
            </div>
          </div>
        </div>


        {/* Reflection Prompts */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-charcoal-800 mb-6">Reflection Prompts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reflectionPrompts.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: category.id * 0.1 }}
                className={`bg-gradient-to-br ${category.color} rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">{category.icon}</span>
                  <h3 className="text-lg font-semibold text-charcoal-800">{category.category}</h3>
                </div>
                <div className="space-y-2">
                  {category.prompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => startNewEntry(prompt, category.category)}
                      className="w-full text-left text-sm text-charcoal-700 hover:text-charcoal-900 p-3 rounded-lg hover:bg-white/50 transition-colors"
                    >
                      "{prompt}"
                    </button>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* My Entries */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-charcoal-800">My Journal Entries</h2>
            <button
              onClick={() => {
                setNewEntry({
                  prompt: '',
                  response: '',
                  mood: '',
                  isPrivate: true,
                  tags: []
                });
                setShowNewEntryModal(true);
              }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg font-medium"
            >
              + Free Write
            </button>
          </div>

          {entries.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-md">
              <div className="text-6xl mb-4">✍️</div>
              <h3 className="text-xl font-semibold text-charcoal-800 mb-2">No entries yet</h3>
              <p className="text-charcoal-600">Start your reflection journey by choosing a prompt above or free writing.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {entries.map((entry) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-purple-100"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        moodOptions.find(m => m.label === entry.mood)?.color || 'bg-gray-100'
                      }`}>
                        {moodOptions.find(m => m.label === entry.mood)?.emoji} {entry.mood}
                      </span>
                      {entry.isPrivate && (
                        <span className="text-xs text-gray-500">🔒 Private</span>
                      )}
                    </div>
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  <div className="text-xs text-gray-500 mb-2">{formatDate(entry.date)}</div>
                  
                  {entry.category && (
                    <div className="text-xs font-medium text-purple-600 mb-2">{entry.category}</div>
                  )}

                  {entry.prompt && (
                    <p className="text-sm font-medium text-charcoal-700 mb-2 italic">"{entry.prompt}"</p>
                  )}

                  <p className="text-sm text-charcoal-600 line-clamp-4 mb-3">{entry.response}</p>

                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {entry.tags.map((tag, idx) => (
                        <span key={idx} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewingEntry(entry)}
                      className="flex-1 text-xs bg-purple-100 text-purple-700 py-2 rounded-lg hover:bg-purple-200 transition-colors font-medium"
                    >
                      Read Full
                    </button>
                    {entry.isPrivate && (
                      <button
                        onClick={() => shareToForum(entry)}
                        className="text-xs bg-pink-100 text-pink-700 px-3 py-2 rounded-lg hover:bg-pink-200 transition-colors font-medium"
                      >
                        Share
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* New Entry Modal */}
        {showNewEntryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-charcoal-800">New Journal Entry</h3>
                <button
                  onClick={() => setShowNewEntryModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {newEntry.prompt && (
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <div className="text-xs font-medium text-purple-600 mb-1">{newEntry.category}</div>
                  <p className="text-sm text-charcoal-700 italic">"{newEntry.prompt}"</p>
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">
                    How are you feeling today? *
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {moodOptions.map((mood) => (
                      <button
                        key={mood.label}
                        onClick={() => setNewEntry({ ...newEntry, mood: mood.label })}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          newEntry.mood === mood.label
                            ? mood.color + ' ring-2 ring-offset-2 ring-purple-500'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {mood.emoji} {mood.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">
                    Your Reflection *
                  </label>
                  <textarea
                    value={newEntry.response}
                    onChange={(e) => setNewEntry({ ...newEntry, response: e.target.value })}
                    rows="8"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="Write your thoughts here... Be honest, be kind to yourself."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-2">
                    Tags (optional)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tagOptions.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                          newEntry.tags.includes(tag)
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newEntry.isPrivate}
                      onChange={(e) => setNewEntry({ ...newEntry, isPrivate: e.target.checked })}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-charcoal-700">
                      Keep this entry private (you can share it later)
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={saveEntry}
                  disabled={!newEntry.response.trim() || !newEntry.mood}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Entry
                </button>
                <button
                  onClick={() => setShowNewEntryModal(false)}
                  className="px-6 py-3 bg-gray-200 text-charcoal-700 rounded-xl hover:bg-gray-300 transition-colors font-semibold"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* View Entry Modal */}
        {viewingEntry && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    moodOptions.find(m => m.label === viewingEntry.mood)?.color || 'bg-gray-100'
                  }`}>
                    {moodOptions.find(m => m.label === viewingEntry.mood)?.emoji} {viewingEntry.mood}
                  </span>
                  {viewingEntry.isPrivate && (
                    <span className="text-sm text-gray-500">🔒 Private</span>
                  )}
                </div>
                <button
                  onClick={() => setViewingEntry(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="text-sm text-gray-500 mb-4">{formatDate(viewingEntry.date)}</div>

              {viewingEntry.category && (
                <div className="text-sm font-medium text-purple-600 mb-3">{viewingEntry.category}</div>
              )}

              {viewingEntry.prompt && (
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-charcoal-700 italic">"{viewingEntry.prompt}"</p>
                </div>
              )}

              <div className="prose prose-sm max-w-none mb-6">
                <p className="text-charcoal-700 whitespace-pre-wrap">{viewingEntry.response}</p>
              </div>

              {viewingEntry.tags && viewingEntry.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {viewingEntry.tags.map((tag, idx) => (
                    <span key={idx} className="text-sm bg-purple-50 text-purple-700 px-3 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {viewingEntry.isPrivate && (
                <button
                  onClick={() => {
                    shareToForum(viewingEntry);
                    setViewingEntry(null);
                  }}
                  className="w-full bg-gradient-to-r from-pink-600 to-rose-600 text-white py-3 rounded-xl hover:from-pink-700 hover:to-rose-700 transition-all font-semibold"
                >
                  Share with Community
                </button>
              )}
            </motion.div>
          </div>
        )}

        {/* Main Alera Welcome Modal (first time after signup) */}
        <WelcomeModal
          isOpen={showAleraWelcome}
          onClose={closeAleraWelcome}
          title="Alera"
          icon="🌱"
        >
          <div className="space-y-3">
            <p className="text-charcoal-600 text-sm leading-relaxed">
              Thank you for being part of our wellness community. Your commitment to healing inspires us, and we're honored to support your journey.
            </p>
            <p className="text-charcoal-500 text-sm leading-relaxed">
              This is your personal wellness command center with everything you need: allergy-friendly recipes, gentle workout programs, supportive community, goal tracking, and curated resources. Every tool is crafted with love to help you thrive - explore at your own pace and remember, you're exactly where you need to be. ✨
            </p>
          </div>
        </WelcomeModal>

        {/* Page-Specific Welcome Modal */}
        <WelcomeModal
          isOpen={showWelcome}
          onClose={closeWelcome}
          title="Check-in Journal"
          icon="📝"
        >
          <div className="space-y-4">
            <p>
              <strong>Welcome to your personal reflection space!</strong> Here's how to use your journal:
            </p>
            <ul className="space-y-2 text-left">
              <li className="flex items-start gap-2">
                <span className="text-purple-600 font-bold">•</span>
                <span><strong>Choose a prompt</strong> from the categories below or click "Free Write" to journal freely</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600 font-bold">•</span>
                <span><strong>Track your mood</strong> with each entry to notice patterns over time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600 font-bold">•</span>
                <span><strong>All entries are private</strong> by default - only you can see them</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-600 font-bold">•</span>
                <span><strong>Share with the community</strong> when you're ready for support and connection</span>
              </li>
            </ul>
            <p className="text-sm text-charcoal-600 italic">
              This is your safe space to reflect, process, and celebrate your wellness journey.
            </p>
          </div>
        </WelcomeModal>
      </div>
    </div>
  );
};

export default CheckInJournal;
